import { Skeleton } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import Calendar, { CalendarProps } from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { useDispatch, useSelector } from 'react-redux';

import { Availability, UnscheduledMeetingSlot } from '@/interface';

import EmptyState from '@/pages/dashboard/project/detail/meetings/components/EmptyState';
import Header from '@/pages/dashboard/project/detail/meetings/components/Header';
import SelectedDateDisplay from '@/pages/dashboard/project/detail/meetings/components/SelectedDateDisplay';
import MeetingCardSkeleton from '@/pages/dashboard/project/detail/meetings/components/meetingSkeleton';
import PendingMeetings from '@/pages/dashboard/project/detail/meetings/components/pendingMeetings.projectdetail';
import '@/pages/dashboard/project/detail/meetings/scheduleMeetings.projectdetail.css';

import { getUnscheduledMeetingsRequest } from '@/redux/slices';
import { AppDispatch, RootState } from '@/redux/store';

dayjs.extend(utc);

const CreateMeetingModal = lazy(() => import('@/pages/dashboard/project/detail/meetings/components/CreateMeetingModal'));
const MeetingCard = lazy(() => import('@/pages/dashboard/project/detail/meetings/components/meetingCard'));

interface TileContentProps {
  date: Date;
  view: string;
}

export const ScheduleMeetingsProjectDetail: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { projectDetail } = useSelector((state: RootState) => state.project);
  const { loading, data } = useSelector((state: RootState) => state.project.getUnscheduledMeetingList);

  const [lastFetchedMonth, setLastFetchedMonth] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [availabilityList, setAvailabilityList] = useState<Availability[]>([]);
  const [search, setSearch] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [viewType, setViewType] = useState<'booked' | 'pending'>('booked');
  const [showCalendar, setShowCalendar] = useState(false);

  const transformMeetings = (meetings: UnscheduledMeetingSlot[]): Availability[] =>
    meetings.map((meet) => ({
      id: meet.id,
      date: dayjs.utc(meet.date).format('YYYY-MM-DD'),
      title: meet.title,
      isBooked: meet.isBooked,
      slots: meet.availableSlot,
      expert: meet.expert,
      bookedSlot: meet.bookedSlot ?? null,
    }));

  // Fetch meetings
  useEffect(() => {
    if (projectDetail?.id && selectedDate) {
      const currentMonth = dayjs.utc(selectedDate).format('YYYY-MM');
      if (lastFetchedMonth !== currentMonth) {
        dispatch(
          getUnscheduledMeetingsRequest({
            id: projectDetail.id,
            date: dayjs.utc(selectedDate).format('YYYY-MM-DD'),
          }),
        );
        setLastFetchedMonth(currentMonth);
      }
    }
  }, [dispatch, projectDetail?.id, selectedDate, lastFetchedMonth]);

  // Update availability list
  useEffect(() => {
    if (data?.length) {
      setAvailabilityList(transformMeetings(data));
    }
  }, [data]);

  const filteredList = useMemo(
    () =>
      availabilityList?.filter((a) => {
        if (!a?.title) return false;
        const matchesSearch = a.title.toLowerCase().includes(search.toLowerCase());
        const matchesType = viewType === 'booked' ? a.isBooked : !a.isBooked;
        return matchesSearch && matchesType;
      }) ?? [],
    [availabilityList, search, viewType],
  );

  const filteredByDate = useMemo(() => {
    if (!selectedDate) return [];
    const dateStr = dayjs(selectedDate).format('YYYY-MM-DD');
    return filteredList.filter((a) => a.date === dateStr);
  }, [selectedDate, filteredList]);

  // Calendar tile markers
  const tileContent = ({ date, view }: TileContentProps) => {
    if (view !== 'month') return null;

    const dateStr = dayjs(date).format('YYYY-MM-DD');
    const meetingsForDate = availabilityList.filter(
      (m) => m.date === dateStr && (viewType === 'booked' ? m.isBooked : !m.isBooked),
    );
    if (!meetingsForDate.length) return null;

    return (
      <div className="flex justify-center mt-1 gap-1">
        {meetingsForDate.map((m, idx) => (
          <div key={idx} className={`w-2 h-2 rounded-full ${m.isBooked ? 'bg-blue-400' : 'bg-gray-400'}`} />
        ))}
      </div>
    );
  };

  const handleDateChange: CalendarProps['onChange'] = (value) => {
    if (!value || Array.isArray(value)) return;
    setSelectedDate(value);
    setShowCalendar(false);
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Header */}
      <Header
        viewType={viewType}
        setViewType={setViewType}
        search={search}
        setSearch={setSearch}
        openModal={() => setIsModalOpen(true)}
      />

      <div className="flex-1 flex flex-col md:flex-row md:gap-8 md:px-6 md:py-4 overflow-hidden relative">
        <div className={`${showCalendar ? 'fixed inset-0 z-40' : 'hidden md:block md:relative'}`}>
          <div
            className={`fixed inset-0 bg-black/50 ${showCalendar ? 'block' : 'hidden'} md:hidden`}
            onClick={() => setShowCalendar(false)}
          />

          {/* Calendar Container */}
          <div className="relative">
            <div
              className={`
                ${showCalendar ? 'fixed z-50' : 'hidden'}
                md:relative md:block
                top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                md:top-6 md:left-0 md:translate-x-0 md:translate-y-0
                transition-all duration-200 ease-in-out
              `}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCalendar(false);
                }}
                className="absolute -top-10 right-0 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white shadow-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors md:hidden"
                aria-label="Close calendar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div
                className="
                  bg-white rounded-xl shadow-lg p-4 w-[90vw] max-w-md max-h-[80vh] overflow-y-auto
                  md:w-72 lg:w-80 md:max-h-none
                  nest-hub:max-h-[500px] nest-hub:overflow-y-auto
                "
                onClick={(e) => e.stopPropagation()}
              >
                <Calendar
                  tileContent={tileContent}
                  onChange={handleDateChange}
                  value={selectedDate}
                  className="custom-calendar w-full text-sm"
                  minDetail="month"
                  next2Label={null}
                  prev2Label={null}
                  navigationLabel={({ date }) => (
                    <span className="text-sm font-medium">
                      {new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(date)}
                    </span>
                  )}
                  formatShortWeekday={(_, date) => ['S', 'M', 'T', 'W', 'T', 'F', 'S'][date.getDay()]}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Meeting Cards */}
        <div className="flex-1 px-4 md:px-0 pb-6 md:pb-0 overflow-y-auto">
          {selectedDate && (
            <SelectedDateDisplay
              date={selectedDate}
              onViewAll={() => setShowCalendar(!showCalendar)}
              showCalendar={showCalendar}
            />
          )}

          <div className="space-y-3 md:space-y-4 h-full overflow-y-auto md:pr-2 pb-20 md:pb-0">
            {loading ? (
              Array.from({ length: 3 }).map((_, idx) => <MeetingCardSkeleton key={idx} />)
            ) : filteredByDate.length ? (
              viewType === 'pending' ? (
                <PendingMeetings
                  pendingMeetings={filteredByDate}
                  projectId={projectDetail?.id || ''}
                  selectedDate={selectedDate!}
                />
              ) : (
                filteredByDate.map((meeting) => (
                  <Suspense fallback={<Skeleton active paragraph={{ rows: 2 }} />} key={meeting.id}>
                    <MeetingCard meeting={meeting} />
                  </Suspense>
                ))
              )
            ) : (
              <EmptyState viewType={viewType} openModal={() => setIsModalOpen(true)} />
            )}
          </div>
        </div>
      </div>

      <Suspense fallback={<div className="text-center py-4">Loading modal...</div>}>
        <CreateMeetingModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          projectId={projectDetail?.id || ''}
          selectedDate={selectedDate!}
        />
      </Suspense>
    </div>
  );
};
